import { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Link, useAlert } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";

import { MemberNotification } from "@/App/api/notification";
import { AuthenticationContext } from "@/App/context/AuthenticationContext";
import { request } from "@/App/api/request";
import { formatDMRConfirmationNumber } from "@/App/utils/directMemberReimbursement";

interface Document {
  id: number;
  name: string;
}

export const DMRMessage: FC<{ readonly memberNotification: MemberNotification }> = ({ memberNotification }) => {
  const { t: translate } = useTranslation();
  const { getAuthToken } = useContext(AuthenticationContext);
  const showAlert = useAlert();

  const documents: Document[] = useMemo(() => {
    return memberNotification.json_message?.documents || [];
  }, [memberNotification]);

  const submissionId = memberNotification.json_message?.submission_id;
  const confirmationNumber = formatDMRConfirmationNumber(memberNotification.json_message?.submission_confirmation_number);

  const handleDocumentDownload = useCallback(
    async (documentId: number) => {
      try {
        const authToken = await getAuthToken();
        const response = await request<{ id: number; submission_id: number }, { url: string }>(
          {
            endpoint: "/dmr/document/url_for_download",
            body: {
              id: documentId,
              submission_id: submissionId,
            },
          },
          authToken,
        );

        if (response.url) {
          window.location.href = response.url;
        }
      } catch (error) {
        console.error("Error downloading document:", error);
        // Log the full error details
        if (error && typeof error === "object") {
          console.error("Error details:", JSON.stringify(error, null, 2));
        }
        showAlert({ type: "error", style: "toast", message: translate("MESSAGE_DETAIL.dmrMessage.downloadError") });
      }
    },
    [getAuthToken, submissionId, showAlert, translate],
  );

  return (
    <Stack testId="dmr-message-container">
      <Stack spacing="small" role="main">
        <h3>{translate("MESSAGE_DETAIL.dmrMessage.successfulSubmission")}</h3>
        <Paragraph>
          {translate("MESSAGE_DETAIL.dmrMessage.confirmationNumber")} <strong>{confirmationNumber}</strong>
        </Paragraph>
        <Paragraph>{translate("MESSAGE_DETAIL.dmrMessage.saveNumber")}</Paragraph>
      </Stack>

      {documents.length > 0 && (
        <Stack spacing="small">
          <Paragraph>{translate("MESSAGE_DETAIL.dmrMessage.downloadDocumentation")}</Paragraph>
          <ul>
            {documents.map((doc) => (
              <li key={doc.id}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDocumentDownload(doc.id);
                  }}
                  style={{ cursor: "pointer", color: "#0066cc", textDecoration: "underline" }}
                >
                  {doc.name}
                </a>
              </li>
            ))}
          </ul>
          <Paragraph>
            {translate("MESSAGE_DETAIL.dmrMessage.filesLocation")} <Link to="/forms-and-documents" size="medium">{translate("MESSAGE_DETAIL.dmrMessage.formsAndDocuments")}</Link> {translate("MESSAGE_DETAIL.dmrMessage.tabOfPortal")}
          </Paragraph>
        </Stack>
      )}
    </Stack>
  );
};
